import { useCallback, useEffect, useRef, useState } from "react";
import { useLocation, useNavigate, useOutletContext } from "react-router-dom";

import { useAuth } from "../auth/AuthProvider";
import { ShellSubrail } from "../components/shell/ShellSubrail";
import { WorkbenchLayout } from "../components/shell/WorkbenchLayout";
import { coverageNavigationSearch } from "../features/manager-calendar/coverage-view-state";
import { fetchShiftTemplateStatusCounts } from "../data/schedule-templates";
import { useReadModel } from "../data/useReadModel";
import { stashPunchHighlight } from "../lib/punch-map-handoff";
import type { PunchMapHighlight } from "../lib/punch-map-highlight";
import {
  clearWizardResume,
  stashWizardResume,
  takeWizardResume,
} from "../lib/schedule-wizard-handoff";
import { isBuilderWeekStart } from "../lib/schedule-wizard-model";
import { weekStartIso } from "../data/schedule-builder";
import {
  scheduleTimesheetsPath,
  scheduleTimesheetsRoute,
  type TimesheetsPreselect,
} from "../lib/schedule-timesheets-route";
import type { ShellOutletContext } from "../nav/AppShell";
import { useSupportSession } from "../support/SupportSessionProvider";
import { MyShiftsPreview } from "../surfaces/MyShiftsPreview";
import { OpenShiftRequestInbox } from "../surfaces/OpenShiftRequestInbox";
import { ScheduleBuilder } from "../surfaces/ScheduleBuilder";
import { ScheduleCoverage } from "../surfaces/ScheduleCoverage";
import { TemplateList } from "../surfaces/TemplateList";
import { SCHEDULE_WIZARD_STEPS, type ScheduleWizardStepKey } from "../surfaces/ScheduleWizard";
import { ScheduleWizardFlow, type ScheduleWizardRouteStep } from "../surfaces/ScheduleWizardFlow";
import { ShiftResponseReport } from "../surfaces/ShiftResponseReport";
import { ServiceWeekApprovalDesk } from "../surfaces/ServiceWeekApprovalDesk";
import { TimeclockLog } from "../surfaces/TimeclockLog";

// Schedule workbench (console shell v3, Wave 1). One WorkbenchLayout with a ShellSubrail:
// Templates / Builder / Requests / Coverage / Roster / Timesheets. Sections switch instead
// of stacking into one endless scroll; every section stays mounted (hidden, not unmounted)
// so in-progress forms, open kits, and polling read models survive a section hop. The head
// metrics strip answers the first-viewport question with honest template counts.
//
// Lifecycle language stays the 2026-08-15 delete/retire lock verbatim (Discard draft vs
// Retire, week bulk-cancel) and the Include retired filter lives inside the Templates list
// (shell v3 lock 8). Wave 2 re-seats those verbs into row overflow menus per the connected
// flow board (rule 6): same confirms, same refusals, same audit events, quieter address.
//
// Wave 2 also opens the wizard route this page owns. `/schedule/*` is a splat, so the step
// lives in the path here rather than in App.tsx, and `scheduleWizardRouteStep` is the one
// reader of it. Slice C mounts ScheduleWizardFlow on that route: the hub CTA and the row
// resume cues enter it, the address carries step + pattern so refresh and Back resume, and
// leaving it lands back on a workbench section with the templates read refreshed.

export const SCHEDULE_WB_SECTIONS = [
  { key: "templates", id: "schedule-templates", label: "Templates" },
  { key: "builder", id: "schedule-builder", label: "Calendar" },
  { key: "requests", id: "schedule-requests", label: "Shift requests" },
  { key: "coverage", id: "schedule-coverage", label: "Coverage" },
  { key: "roster", id: "schedule-myshifts", label: "Roster" },
  { key: "timesheets", id: "schedule-timeclock", label: "Timesheets" },
] as const;

export type ScheduleWbSectionKey = (typeof SCHEDULE_WB_SECTIONS)[number]["key"];

/**
 * Wave 2 wizard route (board Implement rule 1: `/schedule/new?step=define|week|review`).
 *
 * Returns the step the URL asks for, or null when this is not the wizard route. An
 * unknown or missing step resolves to `define` rather than 404-ing the surface: a
 * supervisor who lands on a truncated link should see step 1, not an error.
 */
export function scheduleWizardRouteStep(
  pathname: string,
  search: string,
): ScheduleWizardStepKey | null {
  // Anchored at both ends: an unanchored test would also claim addresses like
  // /setup/schedule/new that this page does not own.
  if (!/^\/schedule\/new\/?$/.test(pathname)) {
    return null;
  }
  const requested = new URLSearchParams(search).get("step");
  const match = SCHEDULE_WIZARD_STEPS.find((entry) => entry.key === requested);
  // `published` is a result, never a link target: nobody may deep-link to proof that no
  // publish produced.
  return match !== undefined && match.key !== "published" ? match.key : "define";
}

/**
 * The wizard address for a step.
 *
 * Deliberately carries no pattern id (lock G-05, decision P-22). The raw row id used to ride
 * in `?template=`, which put a durable internal locator into browser history, the address
 * bar, and every screenshot and pasted link, and invited a hand-edited id onto a step that
 * assumes a loaded shape. The resume target now rides the in-memory handoff in
 * `lib/schedule-wizard-handoff.ts` instead, so the address keeps only what it can honestly
 * own: the step, and the Monday week under review.
 */
export function scheduleWizardPath(
  step: ScheduleWizardRouteStep,
  weekStart: string | null = null,
): string {
  const params = new URLSearchParams({ step });
  if (step === "review" && isBuilderWeekStart(weekStart)) {
    params.set("week", weekStart);
  }
  return `/schedule/new?${params.toString()}`;
}

/** The validated Builder week named by a Review address, or null when it cannot be trusted. */
export function scheduleWizardRouteWeek(search: string): string | null {
  const requested = new URLSearchParams(search).get("week");
  return isBuilderWeekStart(requested) ? requested : null;
}

/** Legacy in-page anchors (guide-dock era) resolve to the section that now owns them. */
const LEGACY_ANCHOR_SECTIONS: Readonly<Record<string, ScheduleWbSectionKey>> = {
  "schedule-templates": "templates",
  "schedule-builder": "builder",
  "schedule-requests": "requests",
  "schedule-coverage": "coverage",
  "schedule-responses": "coverage",
  "schedule-myshifts": "roster",
  "schedule-timeclock": "timesheets",
};

export function SchedulePage() {
  const { client, config, can, accountRole } = useOutletContext<ShellOutletContext>();
  const { user } = useAuth();
  const support = useSupportSession();
  const navigate = useNavigate();
  const location = useLocation();
  const timesheetsRoute = scheduleTimesheetsRoute(location.pathname, location.search);
  // Account + support session fence (SC-17). Workspace switch and act-as start/end clear
  // Coverage and Shift responses without relying on a remount of this page.
  const coverageScopeToken = `${config.accountId}:${support.loading ? "loading" : support.session?.sessionId ?? "direct"}`;
  const [active, setActive] = useState<ScheduleWbSectionKey>(() => {
    if (timesheetsRoute) return "timesheets";
    const anchor = location.hash.replace(/^#/, "");
    return LEGACY_ANCHOR_SECTIONS[anchor] ?? "builder";
  });
  const [timesheetsPreselect, setTimesheetsPreselect] = useState<TimesheetsPreselect | null>(
    () => timesheetsRoute?.preselect ?? null,
  );
  // Honest first-viewport numbers from the existing status column - no new server
  // surface. TemplateList calls `counts.reload` after every mutation it lands, so the
  // strip never contradicts the list rendered directly under it.
  const counts = useReadModel(
    () => fetchShiftTemplateStatusCounts(client, config.accountId),
    [client, config.accountId],
  );
  // Timeclock → Command map (G3a). The coordinate rides an in-memory read-once handoff rather
  // than the URL, so the address bar and browser history never carry a guard's position.
  const showPunchOnMap = useCallback(
    (highlight: PunchMapHighlight) => {
      stashPunchHighlight(highlight);
      navigate("/");
    },
    [navigate],
  );

  // Wave 2 connected wizard (Slice C). The address owns the step and the resume target;
  // this page owns nothing about the flow but where it renders and how it leaves.
  const wizardStep = scheduleWizardRouteStep(location.pathname, location.search);
  const wizardWeekStart =
    wizardStep === "review" ? scheduleWizardRouteWeek(location.search) : null;
  const wizardActive = wizardStep !== null;
  // The resume target lives here, not in the address (G-05/P-22). It is taken from the
  // one-shot handoff on the first render that needs it, then held for as long as the wizard
  // is on screen. A reload has no handoff, so there is no target: the wizard opens on a
  // fresh pattern rather than claiming to resume a row this session never loaded.
  const [wizardTemplateId, setWizardTemplateId] = useState<string | null>(null);
  // Calendar entry carries its week through Define without adding it to the address.
  // Fence the value during render as well as cleanup so a workspace switch cannot seed
  // the new account's wizard with the previous account's selection.
  const [wizardWeekContext, setWizardWeekContext] = useState<{
    accountId: string;
    weekStart: string | null;
  } | null>(null);
  const initialWizardWeekStart =
    wizardWeekContext?.accountId === config.accountId ? wizardWeekContext.weekStart : null;
  useEffect(() => {
    if (!wizardActive) {
      // Leaving the wizard drops the target, so re-entering never inherits the last one.
      setWizardTemplateId(null);
      setWizardWeekContext(null);
      clearWizardResume();
      return;
    }
    // Written unconditionally: no stash for THIS workspace means no target. A workspace
    // switch or a support act-as re-runs this effect without remounting the page, and the
    // previous tenant's pattern must not survive that (the same boundary takeWizardResume
    // enforces on the stash itself).
    const staged = takeWizardResume(config.accountId);
    const stagedWeekStart = staged?.weekStart;
    setWizardTemplateId(staged?.templateId ?? null);
    setWizardWeekContext((current) =>
      current?.accountId === config.accountId
        ? current
        : isBuilderWeekStart(stagedWeekStart)
          ? { accountId: config.accountId, weekStart: stagedWeekStart }
          : null,
    );
  }, [wizardActive, config.accountId]);
  // Deep-link Timesheets under `/schedule/timesheets`. Consume the query once, then strip
  // params so the address never keeps client/site/week after the desk owns its own state.
  useEffect(() => {
    const route = scheduleTimesheetsRoute(location.pathname, location.search);
    if (!route) return;
    setActive("timesheets");
    if (route.preselect !== null) {
      setTimesheetsPreselect(route.preselect);
      navigate(scheduleTimesheetsPath(), { replace: true });
    }
  }, [location.pathname, location.search, navigate]);
  // Remount key for the Templates section: the wizard mutates templates outside the
  // list's own reload seam, so leaving the wizard re-reads the list it changed. The
  // refresh rides the wizardActive FALLING EDGE rather than the exit callbacks, because
  // browser Back out of /schedule/new is also an exit and must not leave a stale list
  // (QA Q4). countsReload rides a ref so the edge effect never refires on read-model
  // identity churn.
  const [templatesEpoch, setTemplatesEpoch] = useState(0);
  const countsReloadRef = useRef(counts.reload);
  countsReloadRef.current = counts.reload;
  const wasWizardActiveRef = useRef(wizardActive);
  useEffect(() => {
    if (wasWizardActiveRef.current && !wizardActive) {
      setTemplatesEpoch((epoch) => epoch + 1);
      countsReloadRef.current();
    }
    wasWizardActiveRef.current = wizardActive;
  }, [wizardActive]);
  const leaveWizard = useCallback(
    (section: ScheduleWbSectionKey, publishedWeekStart?: string) => {
      setActive(section);
      // A newly published shift can be unassigned, so show its service week in Site
      // coverage. Keep only browsing context; no pattern, workspace or shift locators.
      navigate(section === "builder" && isBuilderWeekStart(publishedWeekStart)
        ? `/schedule?${coverageNavigationSearch("", "site", publishedWeekStart)}`
        : "/schedule");
    },
    [navigate],
  );
  const enterWizard = useCallback(
    (
      step: ScheduleWizardRouteStep,
      templateId: string | null,
      weekStart: string | null = null,
    ) => {
      setWizardWeekContext({
        accountId: config.accountId,
        weekStart: isBuilderWeekStart(weekStart) ? weekStart : null,
      });
      // The target rides module state across the navigation; the address never carries it.
      if (templateId !== null && templateId.length > 0) {
        stashWizardResume({ templateId, accountId: config.accountId, weekStart });
        setWizardTemplateId(templateId);
      }
      navigate(scheduleWizardPath(step, weekStart));
    },
    [navigate, config.accountId],
  );

  return (
    <WorkbenchLayout
      className="wb--panes schedule-page"
      subrail={
        <ShellSubrail
          eyebrow="Schedule"
          items={SCHEDULE_WB_SECTIONS.map(({ key, label }) => ({ key, label }))}
          activeKey={wizardActive ? "" : active}
          onSelect={(key) => {
            const next = key as ScheduleWbSectionKey;
            if (wizardActive) {
              leaveWizard(next);
            } else if (timesheetsRoute) {
              setActive(next);
              setTimesheetsPreselect(null);
              navigate("/schedule");
            } else {
              setActive(next);
            }
          }}
        />
      }
      head={
        <div className="wb-head">
          <div className="wb-head__row">
            <h1 className="wb-head__title">Schedule</h1>
            {/* Wave 2 language lock: user chrome speaks the job. Shift pattern leads and
                template survives only where power users need the word. */}
            <p className="wb-head__note">
              Schedule a shift, pick the week it lands on, then watch coverage, responses,
              and the timeclock.
            </p>
          </div>
          {counts.data !== null && !wizardActive && active !== "coverage" ? (
            <dl className="wb-metrics" aria-label="Schedule metrics">
              <div className="wb-metric">
                <dt className="wb-metric__label">Active patterns</dt>
                <dd className="wb-metric__value">{counts.data.active}</dd>
                <dd className="wb-metric__sub">repeating weekly</dd>
              </div>
              <div className="wb-metric">
                <dt className="wb-metric__label">Drafts</dt>
                <dd className="wb-metric__value">{counts.data.draft}</dd>
                <dd className="wb-metric__sub">never published, resumable</dd>
              </div>
              <div className="wb-metric">
                <dt className="wb-metric__label">Retired</dt>
                <dd className="wb-metric__value">{counts.data.retired}</dd>
                <dd className="wb-metric__sub">history kept, read only</dd>
              </div>
            </dl>
          ) : null}
        </div>
      }
    >
      {wizardActive ? (
        <section
          id="schedule-wizard"
          className="wb-section schedule-page__section"
          aria-label="Schedule a shift"
        >
          <div className="shell-pane wb-section__pane">
            <div className="shell-pane__scroll wb-section__scroll">
              <ScheduleWizardFlow
                calendarMode
                key={config.accountId}
                client={client}
                accountId={config.accountId}
                canManage={can("schedule.manage")}
                // The route helper never returns `published` (a result, not an address).
                step={wizardStep as ScheduleWizardRouteStep}
                templateId={wizardTemplateId}
                addressedWeekStart={wizardWeekStart}
                initialWeekStart={initialWizardWeekStart}
                onStepChange={(step, templateId, weekStart) =>
                  enterWizard(step, templateId, weekStart)
                }
                // A draft landed on the server. The id is held in memory so Back and a step
                // change still resume it, and the address is left exactly as it is: there is
                // nothing new for it to say, and a replace here used to be what wrote the
                // raw id into history.
                onTemplateStored={(templateId) => setWizardTemplateId(templateId)}
                onExit={() => leaveWizard("templates")}
                onOpenBuilder={(publishedWeekStart) => leaveWizard("builder", publishedWeekStart)}
              />
            </div>
          </div>
        </section>
      ) : null}

      <section
        id="schedule-templates"
        className="wb-section schedule-page__section"
        aria-labelledby="schedule-templates-title"
        hidden={wizardActive || active !== "templates"}
      >
        <div className="wb-section__intro">
          <h2 id="schedule-templates-title" className="wb-section__title">Shift patterns</h2>
          <p className="page__note">
            Repeating shifts: days, hours, route, guard count. Saved as templates so they can
            repeat any week. Drafts stay private until a week is published. Discard draft
            deletes a never-published draft for good; Retire stops new publishes and keeps
            every existing shift.
          </p>
        </div>
        <div className="shell-pane wb-section__pane">
          <div className="shell-pane__scroll wb-section__scroll">
            <TemplateList
              calendarMode
              key={`templates-${templatesEpoch}`}
              client={client}
              accountId={config.accountId}
              canManage={can("schedule.manage")}
              onShowBuilder={() => setActive("builder")}
              onTemplatesChanged={counts.reload}
              onScheduleShift={() => enterWizard("define", null)}
              onScheduleTemplate={(target) => enterWizard("week", target.templateId)}
            />
          </div>
        </div>
      </section>

      <section
        id="schedule-builder"
        className="wb-section schedule-page__section"
        aria-labelledby="schedule-builder-title"
        hidden={wizardActive || active !== "builder"}
      >
        <div className="wb-section__intro">
          <h2 id="schedule-builder-title" className="wb-section__title">Calendar</h2>
          <p className="page__note">
            Select a person and day, then choose the property, job and times in the side panel.
            Assign a published shift directly, or review an unscheduled draft for new work.
          </p>
        </div>
        <div className="shell-pane wb-section__pane">
          <div className="shell-pane__scroll wb-section__scroll">
            <ScheduleBuilder
              key={coverageScopeToken}
              calendarMode
              onDefineShift={(date) => enterWizard("define", null, weekStartIso(date))}
              client={client}
              accountId={config.accountId}
              timeZone={config.displayTimeZone}
              pollMs={config.refreshMs}
              canManage={can("schedule.manage") && !support.loading && support.session === null}
            />
          </div>
        </div>
      </section>

      <section
        id="schedule-requests"
        className="wb-section schedule-page__section"
        aria-labelledby="schedule-requests-title"
        hidden={wizardActive || active !== "requests"}
      >
        <div className="wb-section__intro">
          <h2 id="schedule-requests-title" className="wb-section__title">Shift requests</h2>
          <p className="page__note">
            Templates set to <strong>Request approval</strong> send guards here instead of letting
            them take a seat outright. Approving creates the assignment; rejecting can carry a
            reason so the guard knows whether to ask again.
          </p>
        </div>
        <div className="shell-pane wb-section__pane">
          <div className="shell-pane__scroll wb-section__scroll">
            <OpenShiftRequestInbox
              client={client}
              accountId={config.accountId}
              timeZone={config.displayTimeZone}
              pollMs={config.refreshMs}
              canManage={can("schedule.manage")}
            />
          </div>
        </div>
      </section>

      <section
        id="schedule-coverage"
        className="wb-section schedule-page__section"
        aria-labelledby="schedule-coverage-title"
        hidden={wizardActive || active !== "coverage"}
      >
        <div className="wb-section__intro">
          <h2 id="schedule-coverage-title" className="wb-section__title">Coverage</h2>
          <p className="page__note">
            Read-only truth about the published schedule: how filled the upcoming slots are, and
            the latest guard acknowledgements and declines from the management-safe response log.
          </p>
        </div>
        <div className="schedule-coverage-stack wb-section__cols">
          <div className="shell-pane wb-section__pane schedule-coverage-stack__slots">
            <div className="shell-pane__head">
              <span className="shell-pane__title">Slot coverage</span>
            </div>
            <div className="shell-pane__scroll wb-section__scroll">
              <ScheduleCoverage
                key={`${coverageScopeToken}:coverage`}
                client={client}
                accountId={config.accountId}
                scopeToken={coverageScopeToken}
                timeZone={config.displayTimeZone}
                pollMs={config.refreshMs}
              />
            </div>
          </div>
          <div className="shell-pane wb-section__pane schedule-coverage-stack__responses" id="schedule-responses">
            <div className="shell-pane__head">
              <span className="shell-pane__title">Shift responses</span>
            </div>
            <div className="shell-pane__scroll wb-section__scroll">
              <ShiftResponseReport
                key={`${coverageScopeToken}:responses`}
                client={client}
                accountId={config.accountId}
                timeZone={config.displayTimeZone}
                pollMs={config.refreshMs}
              />
            </div>
          </div>
        </div>
      </section>

      <section
        id="schedule-myshifts"
        className="wb-section schedule-page__section"
        aria-labelledby="schedule-myshifts-title"
        hidden={wizardActive || active !== "roster"}
      >
        <div className="wb-section__intro">
          <h2 id="schedule-myshifts-title" className="wb-section__title">Roster - guard view preview</h2>
          <p className="page__note">
            What a guard sees - your own assignments only, in the exact shape the guard apps read.
          </p>
        </div>
        <div className="shell-pane wb-section__pane">
          <div className="shell-pane__scroll wb-section__scroll">
            <MyShiftsPreview client={client} accountId={config.accountId} timeZone={config.displayTimeZone} />
          </div>
        </div>
      </section>

      <section
        id="schedule-timeclock"
        className="wb-section schedule-page__section"
        aria-labelledby="schedule-timeclock-title"
        hidden={wizardActive || active !== "timesheets"}
      >
        <div className="wb-section__intro">
          <h2 id="schedule-timeclock-title" className="wb-section__title">Timesheets</h2>
          <p className="page__note">
            Review a completed service week and freeze its facts for proof of service, including
            the exceptions recorded. This approval does not approve payroll hours.
          </p>
        </div>
        <div className="shell-pane wb-section__pane">
          <div className="shell-pane__scroll wb-section__scroll">
            <ServiceWeekApprovalDesk
              client={client}
              actorId={user?.id ?? null}
              accountId={config.accountId}
              accountRole={accountRole}
              canReview={can("timeclock.manage") && can("schedule.view") && can("evidence.view")}
              active={!wizardActive && active === "timesheets"}
              preselect={timesheetsPreselect}
              onPreselectConsumed={() => setTimesheetsPreselect(null)}
            />
            <h3 className="service-week__raw-heading">
              Recent punch log · all sites · {config.displayTimeZone} · read-only
            </h3>
            <TimeclockLog
              client={client}
              accountId={config.accountId}
              timeZone={config.displayTimeZone}
              pollMs={config.refreshMs}
              onShowPunchOnMap={showPunchOnMap}
            />
          </div>
        </div>
      </section>
    </WorkbenchLayout>
  );
}
